import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { createTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';

// function LinkBehaviour avoids having to do LinkBehavior.displayName = 'LinkBehavior';
const LinkBehavior = forwardRef(function LinkBehavior(props, ref) {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  //If href starts with http or https, then it is an external link
  if (href.startsWith('http')) {
    return <a href={href} ref={ref} {...other} />;
  }
  return <NavLink ref={ref} to={href} {...other} />;
});

LinkBehavior.propTypes = {
  href: PropTypes.oneOfType([
    PropTypes.shape({
      hash: PropTypes.string,
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
    PropTypes.string,
  ]).isRequired,
};

const primaryColorMain = '#054B60';
const secondaryColorMain = '#685F12';
const secondaryColorLight = '#C4B421';
const tertiary = '#fff';


const theTheme = createTheme({
  pamGrid: {
    '& .pam-grid-header': {
      backgroundColor: primaryColorMain,
      color: 'white'
    },
    '& .MuiDataGrid-iconButtonContainer > .MuiButtonBase-root': {
      color: 'white'
    },
    '& .MuiDataGrid-menuIcon > .MuiButtonBase-root': {
      color: 'white'
    },
    '& .row-odd': {
      backgroundColor: '#e6ecf2',
    }
  },
  singleFlexRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between'
  },
  actionLinkRow: {
    color: secondaryColorMain,
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block',
    lineHeight: '1.75',
    marginRight: '10px',
    marginLeft: '5px',
  },
  cardContent: {
    fontSize: '12px',
    marginLeft: '5px',
    marginRight: '5px',
  },

  inspector: {
    icon: {
      color: secondaryColorMain,
      fontSize: '16px',
      marginRight: '2px'
    },
    heading: {
      fontFamily: 'sans-serif',
      fontSize: '22px',
      fontWeight: 'bold',
      color: primaryColorMain,
    },
    clearButton: {
      fontSize: '12px',
      marginBottom: '5px'
    },
    cardHeader: {
      lineHeight: '1.75',
      marginRight: '10px',
      marginLeft: '5px',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    cardFooter: {
      lineHeight: '1.75',
      marginRight: '10px',
      marginLeft: '5px',
    },
    cardFooterLinks: {
      color: secondaryColorMain,
      fontSize: '12px',
      fontWeight: 'bold',
      display: 'inline-block',
    },
    featuredCard: {
      heading: { fontFamily: 'sans-serif', fontWeight: 'bold', fontSize: '14px', color: primaryColorMain, marginTop: '10px' },
      legend: { fontFamily: 'sans-serif', fontWeight: 'bold', fontSize: '32px', color: '#434343', marginBottom: '20px' },
    }
  },
  filterableCard: {
    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingBottom: '4px',
      borderBottom: '2px solid #C8C8C8'
    }
  },

  palette: {
    text: {
      header: primaryColorMain,
      special: '##C3B42F',
    },
    inspectorItem: {
      outer: {
        border: '1px solid black',
        borderRadius: '5px',
        marginBottom: '10px',
        marginTop: '15px',
      },
      inner: {
        padding: '0.5em',
      },
      divider: {
        backgroundColor: 'black',
      }
    },
    mapSidebar: {
      background: '#2B5C92',
      selectedBackground: '#054B60',
      text: '#ffffff',
      selectedText: '#ffffff',
    },
    slider: {
      default: '#33CF4D',
      border: '6px solid #fff'
    },
    background: {
      default: '#F2F4F3',
    },
    hover: {
      dark: '#001F35',
    },
    primary: {
      main: primaryColorMain,
      light: '#027AC8',
      text: '#ffffff',
    },
    secondary: {
      main: secondaryColorLight,
      light: '#F68802',
      text: '#ffffff',
    },
    regressive: {
      main: '#46400C',
      light: '#F68802',
      dark: '#685F12',
      text: '#ffffff',
      contrastText: '#ffffff',
    },
    tertiary: {
      main: tertiary,
      contrastText: '#FFFFFF',
      text: '#FFFFFF',
      light: '#c5c871',
      dark: primaryColorMain,
      lightBlue: '#2B5C92'
    },
    accent: {
      main: '#FFFFFF',
      contrastText: '#1F4765',
    },
    grayscale: {
      main: '#979797',
      light: '#D8D8D8',
      lighter: '#F1F1F1',
      lightest: '#C8C8C8',
    },
    toggleBackground: {
      main: '#A5FF98',
    },
  },
  typography: {
    fontFamily: ['"PT Sans"', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    navLink: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
      color: `${secondaryColorMain} !important`,
      textDecorationColor: `${secondaryColorMain} !important`,
      marginTop: 2,
      marginBottom: 2,
    },
    subHeader: {
      fontSize: '0.875rem',
      fontWeight: 'bold',
      color: primaryColorMain,
      marginTop: 2,
      marginBottom: 2,
    },
    inspector: {
      fontSize: '0.875rem',
      marginTop: '8px',
      marginBottom: '8px',
    },
    lineItem: {
      fontSize: '0.875rem',
      marginTop: '8px',
      marginBottom: '8px',
    },
    sectionHeader: {
      fontSize: '1rem',
      fontWeight: 'bold',
      color: primaryColorMain,
      marginTop: 2,
      marginBottom: 2,
    },
    panelHeader: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      color: primaryColorMain,
      marginTop: 2,
      marginBottom: 2,
    },
    modalTitle: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: primaryColorMain,
      marginTop: '20px',
      marginBottom: '0',
      textAlign: 'center',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          sectionHeader: 'h1',
          subHeader: 'h2',
          modalTitle: 'h2',
          panelHeader: 'h2',
          inspector: 'p',
          navLink: 'a'
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: '#434343',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        h1: {
          fontSize: '16px',
          color: primaryColorMain,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
        underline: 'hover',
        color: `${secondaryColorMain} !important`
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
      },
      variants: [
        {
          props: {
            variant: 'contained',
          },
          style: {
            borderRadius: 28,
          },
        },
        {
          props: {
            variant: 'user',
          },
          style: {
            background: 'none'
          },
        },
        {
          props: {
            variant: 'panel',
          },
          style: {
            display: 'block',
            borderRadius: '0px',
            textTransform: 'none',
            marginRight: '0px !important',
            marginLeft: '0px !important',
            color: primaryColorMain,
            '&.active': {
              // background: '#EBEEF1',
              background: '#FFF',
              cursor: 'not-allowed',
            },
          },
        },
        {
          props: {
            variant: 'appbar',
          },
          style: {
            color: 'white',
            borderRadius: '0px',
            background: 'none',
            '&.active': {
              borderBottom: '2px solid',
              borderColor: secondaryColorLight,
            },
          },
        },
        {
          props: {
            variant: 'dashed',
          },
          style: {
            textTransform: 'none',
            border: '2px dashed grey',
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: primaryColorMain,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '14px',
        }
      }
    },
  },
});

export default theTheme;
